import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal, Select } from '@components/common/UIComponents';
import {
  addSleepEntry,
  getSleepForDate,
  getSleepInRange,
  deleteSleepEntry,
  getUserSettings
} from '@services/firestoreService';
import { Moon, Plus, Trash2, ChevronLeft, ChevronRight, Award, Clock, TrendingUp } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, eachDayOfInterval, differenceInHours, differenceInMinutes, parseISO } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SLEEP_QUALITY = [
  { value: 5, label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/20' },
  { value: 4, label: 'Good', color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/20' },
  { value: 3, label: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/20' },
  { value: 2, label: 'Poor', color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/20' },
  { value: 1, label: 'Very Poor', color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/20' }
];

const SleepPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sleepEntries, setSleepEntries] = useState([]); // Today's entries
  const [periodData, setPeriodData] = useState([]); // Period entries for charts
  const [showAddModal, setShowAddModal] = useState(false);
  const [sleepGoal, setSleepGoal] = useState(8); // Default 8 hours

  const [formData, setFormData] = useState({
    bedTime: '',
    wakeTime: '',
    quality: 3,
    notes: ''
  });

  useEffect(() => {
    loadSleepData();
  }, [user, period, currentDate]);

  const getPeriodRange = () => {
    if (period === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (period === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const loadSleepData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load user settings for goal
      const settings = await getUserSettings(user.uid);
      if (settings?.sleepGoal) {
        setSleepGoal(settings.sleepGoal);
      }

      // Load today's entries
      const todayEntries = await getSleepForDate(user.uid, new Date());
      setSleepEntries(todayEntries);

      // Load period data
      const { start, end } = getPeriodRange();
      const periodEntries = await getSleepInRange(user.uid, start, end);
      setPeriodData(periodEntries);
    } catch (error) {
      console.error('Error loading sleep data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (period === 'day') {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1));
    } else if (period === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const getPeriodLabel = () => {
    if (period === 'day') {
      return format(currentDate, 'MMMM dd, yyyy');
    } else if (period === 'week') {
      const { start, end } = getPeriodRange();
      return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const calculateSleepDuration = (bedTime, wakeTime) => {
    if (!bedTime || !wakeTime) return 0;
    
    const bed = new Date(bedTime);
    const wake = new Date(wakeTime);
    
    // If wake time is before bed time, add a day to wake time
    if (wake < bed) {
      wake.setDate(wake.getDate() + 1);
    }
    
    const hours = differenceInHours(wake, bed);
    const minutes = differenceInMinutes(wake, bed) % 60;
    
    return parseFloat((hours + minutes / 60).toFixed(1));
  };

  const handleAddSleep = async () => {
    if (!formData.bedTime || !formData.wakeTime) return;

    try {
      const duration = calculateSleepDuration(formData.bedTime, formData.wakeTime);
      
      await addSleepEntry(user.uid, {
        bedTime: formData.bedTime,
        wakeTime: formData.wakeTime,
        duration,
        quality: parseInt(formData.quality),
        notes: formData.notes
      });

      setShowAddModal(false);
      setFormData({
        bedTime: '',
        wakeTime: '',
        quality: 3,
        notes: ''
      });
      loadSleepData();
    } catch (error) {
      console.error('Error adding sleep entry:', error);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await deleteSleepEntry(user.uid, entryId);
      loadSleepData();
    } catch (error) {
      console.error('Error deleting sleep entry:', error);
    }
  };

  // Calculate today's totals
  const todayTotalHours = sleepEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
  const todayAvgQuality = sleepEntries.length > 0 
    ? sleepEntries.reduce((sum, entry) => sum + (entry.quality || 0), 0) / sleepEntries.length 
    : 0;
  const progress = (todayTotalHours / sleepGoal) * 100;

  // Calculate period statistics
  const sleepByDay = useMemo(() => {
    const { start, end } = getPeriodRange();
    const allDays = eachDayOfInterval({ start, end });
    
    return allDays.map(day => {
      const dayId = format(day, 'yyyy-MM-dd');
      const dayEntries = periodData.filter(entry => {
        const entryDate = entry.date ? new Date(entry.date) : new Date();
        return format(entryDate, 'yyyy-MM-dd') === dayId;
      });
      
      const dayTotal = dayEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
      const dayAvgQuality = dayEntries.length > 0
        ? dayEntries.reduce((sum, e) => sum + (e.quality || 0), 0) / dayEntries.length
        : 0;
      
      return {
        date: day,
        dateId: dayId,
        duration: dayTotal,
        quality: dayAvgQuality,
        entries: dayEntries
      };
    });
  }, [period, currentDate, periodData, sleepGoal]);

  // Calculate statistics
  const sleepStats = {
    totalHours: sleepByDay.reduce((sum, day) => sum + day.duration, 0),
    average: parseFloat((sleepByDay.reduce((sum, day) => sum + day.duration, 0) / sleepByDay.length).toFixed(1)) || 0,
    best: Math.max(...sleepByDay.map(day => day.duration), 0),
    daysAchieved: sleepByDay.filter(day => day.duration >= sleepGoal).length,
    avgQuality: parseFloat((sleepByDay.reduce((sum, day) => sum + day.quality, 0) / sleepByDay.filter(d => d.quality > 0).length).toFixed(1)) || 0
  };

  // Prepare chart data
  const getChartLabels = () => {
    if (period === 'day') {
      return ['Today'];
    } else if (period === 'week') {
      return sleepByDay.map(day => format(day.date, 'EEE')); // Mon, Tue, Wed...
    } else {
      // For month, show every few days
      return sleepByDay.filter((_, index) => index % Math.ceil(sleepByDay.length / 10) === 0)
                    .map(day => format(day.date, 'MMM d'));
    }
  };

  const getChartData = () => {
    if (period === 'day') {
      const todayData = sleepByDay.find(day => 
        format(day.date, 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
      );
      return [todayData?.duration || 0];
    }
    
    return sleepByDay.map(day => day.duration);
  };

  const getQualityColor = (quality) => {
    if (quality >= 4.5) return 'rgba(34, 197, 94, 0.8)'; // green
    if (quality >= 3.5) return 'rgba(59, 130, 246, 0.8)'; // blue
    if (quality >= 2.5) return 'rgba(234, 179, 8, 0.8)'; // yellow
    if (quality >= 1.5) return 'rgba(249, 115, 22, 0.8)'; // orange
    return 'rgba(239, 68, 68, 0.8)'; // red
  };

  const sleepChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Sleep Duration (hours)',
        data: getChartData(),
        backgroundColor: sleepByDay.map(day => 
          day.duration >= sleepGoal ? 'rgba(139, 92, 246, 0.8)' : 'rgba(156, 163, 175, 0.5)'
        ),
        borderColor: sleepByDay.map(day => 
          day.duration >= sleepGoal ? 'rgb(139, 92, 246)' : 'rgb(156, 163, 175)'
        ),
        borderWidth: 1
      }
    ]
  };

  const qualityChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Sleep Quality',
        data: sleepByDay.map(day => day.quality),
        backgroundColor: sleepByDay.map(day => getQualityColor(day.quality)),
        borderColor: sleepByDay.map(day => getQualityColor(day.quality).replace('0.8', '1')),
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            if (context.dataset.label.includes('Duration')) {
              return `Goal: ${sleepGoal} hours`;
            }
            return '';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value}h`
        }
      }
    }
  };

  const getQualityLabel = (quality) => {
    const q = SLEEP_QUALITY.find(sq => sq.value === Math.round(quality));
    return q ? q.label : 'N/A';
  };

  const getQualityStyle = (quality) => {
    const q = SLEEP_QUALITY.find(sq => sq.value === Math.round(quality));
    return q ? { color: q.color, bg: q.bg } : { color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Sleep Tracking</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track your sleep patterns and quality
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus size={20} className="mr-2" />
          Log Sleep
        </Button>
      </div>

      {/* Period Selector */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Period Tabs */}
          <div className="flex gap-2">
            <Button
              variant={period === 'day' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('day')}
            >
              Daily
            </Button>
            <Button
              variant={period === 'week' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('week')}
            >
              Weekly
            </Button>
            <Button
              variant={period === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('month')}
            >
              Monthly
            </Button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
              {getPeriodLabel()}
            </span>
            <button
              onClick={() => handleNavigate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={format(currentDate, 'yyyy-MM-dd') >= format(new Date(), 'yyyy-MM-dd')}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </Card>

      {/* Today's Summary */}
      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 bg-purple-100 dark:bg-purple-900/20 rounded-xl">
            <Moon className="text-purple-600 dark:text-purple-400" size={32} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Last Night's Sleep
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {format(new Date(), 'MMMM dd, yyyy')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="flex items-end justify-between mb-2">
              <span className="text-5xl font-bold text-gray-900 dark:text-gray-100">
                {todayTotalHours.toFixed(1)}
                <span className="text-2xl text-gray-500 dark:text-gray-400 ml-2">hrs</span>
              </span>
              <span className="text-lg text-gray-600 dark:text-gray-400">
                / {sleepGoal}h
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
              <div
                className="bg-gradient-to-r from-purple-400 to-purple-600 h-4 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {progress >= 100 ? '🎉 Sleep goal achieved!' : `${Math.round(progress)}% of sleep goal`}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Sleep Quality</p>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 rounded-lg ${getQualityStyle(todayAvgQuality).bg}`}>
                <span className={`text-3xl font-bold ${getQualityStyle(todayAvgQuality).color}`}>
                  {todayAvgQuality > 0 ? todayAvgQuality.toFixed(1) : '--'}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400"> / 5</span>
              </div>
              <span className={`text-lg font-medium ${getQualityStyle(todayAvgQuality).color}`}>
                {todayAvgQuality > 0 ? getQualityLabel(todayAvgQuality) : 'No data'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Period Statistics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Sleep Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {sleepStats.totalHours.toFixed(1)}h
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Sleep</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {sleepStats.average}h
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg/Night</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {sleepStats.best.toFixed(1)}h
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Best Night</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-2xl font-bold text-purple-600 dark:text-purple-400">
                <Award size={24} />
                <span>{sleepStats.daysAchieved}</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days Met Goal</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getQualityStyle(sleepStats.avgQuality).color}`}>
                {sleepStats.avgQuality > 0 ? sleepStats.avgQuality : '--'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg Quality</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Sleep Duration Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Sleep Duration Overview
        </h3>
        <div className="h-64">
          <Bar data={sleepChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Sleep Quality Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Sleep Quality Overview
        </h3>
        <div className="h-64">
          <Bar data={qualityChartData} options={{
            ...chartOptions,
            scales: {
              y: {
                beginAtZero: true,
                max: 5,
                ticks: {
                  stepSize: 1
                }
              }
            }
          }} />
        </div>
      </Card>

      {/* Recent Sleep Entries */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Recent Sleep Logs ({sleepEntries.length})
        </h3>

        {sleepEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Moon size={48} className="mx-auto mb-3 opacity-30" />
            <p>No sleep logged yet</p>
            <p className="text-sm mt-1">Start tracking your sleep patterns</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sleepEntries.map((entry) => {
              const qualityInfo = SLEEP_QUALITY.find(q => q.value === entry.quality);
              return (
                <div
                  key={entry.id}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock size={20} className="text-purple-600 dark:text-purple-400" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {entry.duration.toFixed(1)} hours
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {format(new Date(entry.bedTime), 'h:mm a')} → {format(new Date(entry.wakeTime), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-8">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${qualityInfo?.bg} ${qualityInfo?.color}`}>
                          {qualityInfo?.label}
                        </span>
                        {entry.notes && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add Sleep Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Log Sleep"
      >
        <div className="space-y-4">
          <Input
            type="datetime-local"
            label="Bed Time"
            value={formData.bedTime}
            onChange={(e) => setFormData({ ...formData, bedTime: e.target.value })}
            fullWidth
          />
          <Input
            type="datetime-local"
            label="Wake Time"
            value={formData.wakeTime}
            onChange={(e) => setFormData({ ...formData, wakeTime: e.target.value })}
            fullWidth
          />
          <Select
            label="Sleep Quality"
            value={formData.quality}
            onChange={(e) => setFormData({ ...formData, quality: e.target.value })}
            options={SLEEP_QUALITY}
            fullWidth
          />
          <Input
            type="text"
            label="Notes (optional)"
            placeholder="e.g., Had trouble falling asleep..."
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
          />
          <div className="flex gap-3">
            <Button onClick={handleAddSleep} fullWidth>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SleepPage;
